import React, {useState, useCallback, useRef} from 'react';
import styled, {css} from 'styled-components';
import {useIntl} from 'react-intl';
import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import {BookmarkIcon, BOOKMARK_COLOR_DISABLED} from '../shared/icons/BookmarkIcon';
import {chatHeadingFont, D} from '../ai-chat/design-tokens';
import KebabMenu from './KebabMenu';
import PromptDropdown from './PromptDropdown';
import {PromptDropdown_viewer$key} from './__generated__/PromptDropdown_viewer.graphql';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';

const {font} = tokensForThemeProvider.sky.sys;

const labelFont = css`
	${font.label['1']}
`;

const Header = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	height: 60px;
	padding: 0 24px;
	border-bottom: 1px solid ${D.inputBorder};
	flex-shrink: 0;
`;

const Title = styled.span`
	${labelFont}
	${chatHeadingFont}
	flex: 1;
	color: ${D.v2.textPrimary};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const IconBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border: none;
	background: transparent;
	border-radius: 8px;
	cursor: pointer;
	color: ${D.v2.textPrimary};
	flex-shrink: 0;
	overflow: clip;

	&:hover {
		background: ${D.v2.surfaceHover};
	}
`;

const PromptIconBtn = styled(IconBtn)`
	color: ${D.v2.iconFillDisabled};
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

type DrawerHeaderProps = {
	onClose: () => void;
	onClearChat: () => void;
	onRegenerateLastReply: () => void;
	canRegenerate: boolean;
	viewerRef: PromptDropdown_viewer$key | null;
};

const DrawerHeader = ({
	onClose,
	onClearChat,
	onRegenerateLastReply,
	canRegenerate,
	viewerRef,
}: DrawerHeaderProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {savedPromptTexts} = useAgentDrawerContext();
	const [isPromptDropdownOpen, setIsPromptDropdownOpen] = useState(false);
	const [isKebabOpen, setIsKebabOpen] = useState(false);
	const promptBtnRef = useRef<HTMLButtonElement>(null);
	const kebabBtnRef = useRef<HTMLButtonElement>(null);
	const handleCloseDropdown = useCallback(() => setIsPromptDropdownOpen(false), []);
	const handleCloseKebab = useCallback(() => setIsKebabOpen(false), []);

	return (
		<Header>
			<Title>{formatMessage({id: 'nova.header.title'})}</Title>
			<Actions>
				<PromptIconBtn
					ref={promptBtnRef}
					type="button"
					aria-label={formatMessage({id: 'nova.header.prompt_library'})}
					title={formatMessage({
						id:
							savedPromptTexts.size > 0
								? 'nova.header.prompt_library_tooltip'
								: 'nova.header.prompt_library_tooltip_empty',
					})}
					onClick={() => setIsPromptDropdownOpen(prev => !prev)}
				>
					<BookmarkIcon color={BOOKMARK_COLOR_DISABLED} />
				</PromptIconBtn>
				<IconBtn
					ref={kebabBtnRef}
					type="button"
					aria-label={formatMessage({id: 'nova.header.kebab_label'})}
					onClick={() => setIsKebabOpen(prev => !prev)}
				>
					<Icon icon="more" size="m" />
				</IconBtn>
				<IconBtn type="button" aria-label={formatMessage({id: 'nova.drawer.close_label'})} onClick={onClose}>
					<Icon icon="close" size="m" />
				</IconBtn>
			</Actions>
			{isPromptDropdownOpen && (
				<PromptDropdown viewerRef={viewerRef} onClose={handleCloseDropdown} excludeRef={promptBtnRef} />
			)}
			{isKebabOpen && (
				<KebabMenu
					onClearChat={onClearChat}
					onRegenerateLastReply={onRegenerateLastReply}
					canRegenerate={canRegenerate}
					onClose={handleCloseKebab}
					excludeRef={kebabBtnRef}
				/>
			)}
		</Header>
	);
};

export default DrawerHeader;
